import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import CloseIcon from '@mui/icons-material/Close';
import BugReportIframe from "../utils/BugReportIframe";

interface State {
  key: string;
  code: string;
  name: string;
}

export default function Footer() {
  const location = useLocation();
  const { pathname } = location;
  const isEmbedMode = new URLSearchParams(location.search).get("embed") === "true";
  const isInIframe = window.self !== window.top;
  const apiUrl = process.env.REACT_APP_API_URL;
  const shouldHide =
    isEmbedMode ||
    isInIframe ||
    pathname.includes("admin") ||
    pathname.includes("partner") ||
    pathname.includes("supervisor") ||
    pathname.includes("canvasser") ||
    pathname.includes("about") ||
    pathname.includes("password") ||
    pathname === "/";
  const [states, setStates] = useState<State[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);

  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleReportProblem = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  useEffect(() => {
    if (shouldHide) {
      setLoading(false);
      return;
    }

    const fetchStates = async () => {
      try {
        const response = await axios.get(`${apiUrl}/elections/states`);
        if (response.data.success) {
          setStates(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching states:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStates();
  }, [apiUrl, shouldHide]);

  const filteredStates = states.filter((state) =>
    state.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStateFlagUrl = (stateCode: string) => {
    return `https://flagcdn.com/48x36/us-${stateCode.toLowerCase()}.png`;
  };

  if (shouldHide) {
    return null;
  }

  return (
    <footer className="bg-white text-black border-t border-neutral-200 shadow-lg pt-12 pb-6 px-2">
      <div className="max-w-6xl mx-auto flex flex-col items-center">
        {/* Heading */}
        <h2 className="text-2xl md:text-3xl  text-center mb-4 font-bold tracking-wide">
          Find everything you need to vote in our state election centers
        </h2>

        <p className="text-sm text-center px-3 pb-4">Official registration  dates, polling locations and ballot information  tailored to  your specific state</p>

        {/* Search Box */}
        <div className="w-full max-w-3xl mx-auto mt-4 mb-8">
          <div className="relative">
            <input
              type="text"
              placeholder="Search states..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-3 pl-10 pr-4 text-gray-700 bg-white border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors"
            />
            <svg
              className="absolute left-3 top-3.5 w-5 h-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
        </div>

        {/* States Grid */}
        {loading ? (
          <div className="w-full flex justify-center mb-8">
            <div className="text-gray-500">Loading states...</div>
          </div>
        ) : (
          <div className="w-full flex justify-center mb-8">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {filteredStates.map((state) => (
                <Link
                  key={state?.code}
                  to={`/state/${state?.code}`}
                  className="group flex flex-col items-center p-4 bg-white border border-gray-200 rounded-lg hover:border-blue-300 hover:shadow-md transition-all duration-200 text-center"
                >
                  <div className="w-10 h-6 mb-2 flex items-center justify-center">
                    <img
                      src={getStateFlagUrl(state.code)}
                      alt={`${state.name} flag`}
                      className="w-full h-full object-cover rounded shadow-sm"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='48' height='32' viewBox='0 0 48 32'%3E%3Crect width='48' height='32' fill='%23e5e7eb'/%3E%3Ctext x='24' y='20' text-anchor='middle' font-family='Arial' font-size='8' fill='%236b7280'%3EFlag%3C/text%3E%3C/svg%3E";
                      }}
                    />
                  </div>
                  <div className="font-medium text-sm md:text-base text-gray-800 group-hover:text-blue-700 transition-colors">
                    {state.name}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {state.code}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {filteredStates.length === 0 && !loading && (
          <div className="w-full flex justify-center mb-8">
            <div className="text-gray-500">No states found matching "{searchTerm}"</div>
          </div>
        )}

        {/* Decorative divider */}
        <div className="w-full flex justify-center">
          <div className="h-[2px] bg-gradient-to-r from-black/0 via-black/40 to-black/0 w-1/2 rounded-full mb-8" />
        </div>

        {/* Footer Links Section */}
        <div className="w-full max-w-6xl mx-auto mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Quick Links */}
            <div className="text-center md:text-left">
              <h3 className="text-lg font-semibold mb-4 text-black">Quick Links</h3>
              <ul className="space-y-2">
                <li>
                  <Link
                    to="/partner/login"
                    className="text-gray-600 hover:text-blue-600 transition-colors duration-200 text-sm"
                  >
                    Partner Login
                  </Link>
                </li>
                <li>
                  <Link
                    to="/canvasser/login"
                    className="text-gray-600 hover:text-blue-600 transition-colors duration-200 text-sm"
                  >
                    Canvasser Login
                  </Link>
                </li>
                <li>
                  <Link
                    to="/register-to-vote"
                    className="text-gray-600 hover:text-blue-600 transition-colors duration-200 text-sm"
                  >
                    Register to Vote
                  </Link>
                </li>
                <li>
                  <Link
                    to="/about"
                    className="text-gray-600 hover:text-blue-600 transition-colors duration-200 text-sm"
                  >
                    About
                  </Link>
                </li>
              </ul>
            </div>

            {/* Legal & Support */}
            <div className="text-center md:text-left">
              <h3 className="text-lg font-semibold mb-4 text-black">Legal & Support</h3>
              <ul className="space-y-2">
                <li>
                  <Link
                    to="/privacy-policy"
                    className="text-gray-600 hover:text-blue-600 transition-colors duration-200 text-sm"
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  >
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link
                    to="/faq"
                    className="text-gray-600 hover:text-blue-600 transition-colors duration-200 text-sm"
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  >
                    FAQ
                  </Link>
                </li>
                {pathname !== "/check-registration" && (<li>
                  <Link
                    to="/terms-website"
                    className="text-gray-600 hover:text-blue-600 transition-colors duration-200 text-sm"
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  >
                    Terms and Conditions (Website)
                  </Link>
                </li>)}
                <li>
                  <Link
                    to="/terms-platform"
                    className="text-gray-600 hover:text-blue-600 transition-colors duration-200 text-sm"
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  >
                    Terms and Conditions (Platform)
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Copyright and Brand */}
        <div className="w-full flex flex-col md:flex-row items-center justify-between">
          <div className="flex items-center space-x-2 mb-2 md:mb-0">
            <img
              src={require("../assets/images/123votelogo.png")}
              alt="123-VOTE Logo"
              className="w-8 h-8 object-contain"
            />
            <span className="font-bold tracking-widest text-black/80">123-VOTE</span>
          </div>
          <button
            onClick={handleReportProblem}
            className="text-gray-500 hover:text-blue-700 text-xs font-medium"
          >
            Report a problem
          </button>
          <div className="text-xs text-gray-500 text-center md:text-right">
            &copy; {new Date().getFullYear()} 123-VOTE. All rights reserved.
          </div>
        </div>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-4 ">
              <h2 className="text-xl font-semibold text-gray-900">Report a Problem</h2>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <CloseIcon />
              </button>
            </div>
            <div className="p-4">
              <BugReportIframe />
            </div>
          </div>
        </div>
      )}
    </footer>
  );
}
